import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiDownload,
  FiPrinter,
  FiBox,
  FiTruck,
  FiPackage,
  FiRotateCcw,
} from "react-icons/fi";
import { challanApi } from "./challanApi";
import { toast } from "react-toastify";
import PdfPreviewModal from "../../components/PdfPreviewModal";
import { BASE_URL } from "../../config/apiconfig";
import "../../style/Challanviewdialog.css";

const typeChipClass = (type) => {
  if (type === "DELIVERED") return "cv-chip cv-chip--delivered";
  if (type === "RECEIVED") return "cv-chip cv-chip--received";
  if (type === "COMBINED") return "cv-chip cv-chip--combined";
  return "cv-chip";
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const Field = ({ label, value, children }) => (
  <div className="cv-field">
    <span>{label}</span>
    {children || <strong title={value || undefined}>{value || <span className="cv-empty">Not Available</span>}</strong>}
  </div>
);

const ChallanViewDialog = ({ open, onClose, challanId }) => {
  const [challan, setChallan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const printableRef = useRef(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!challanId) {
        console.log('No challanId provided');
        return;
      }

      setLoading(true);
      setChallan(null);

      try {
        const response = await challanApi.getChallanById(challanId);
        setChallan(response.data || response);
      } catch (error) {
        console.error("Failed to fetch challan details:", error);
        toast.error("Failed to load challan details.");
        setChallan(null);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchDetails();
    }
  }, [challanId, open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handleDownload = async () => {
    if (!challanId) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/challans/download/${challanId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfModalOpen(true);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to load challan PDF.");
    }
  };

  const handlePrint = () => {
    const printContent = printableRef.current;
    if (!printContent) return;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload(); // Restores React event listeners after the raw HTML swap
  };

  return createPortal(
    <div className="cv-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cv-modal" role="dialog" aria-modal="true" aria-label="Challan Details">
        {/* Premium Header */}
        <div className="cv-modal-header">
          <div className="cv-header-main">
            <div className="cv-header-title">
              <div className="cv-header-icon">
                <FiPackage size={18} />
              </div>
              <h3>{challan?.challanNumber || "—"}</h3>
              {challan && (
                <>
                  <span className={typeChipClass(challan.challanType)}>{challan.challanType || "—"}</span>
                  <span className="cv-status-chip">Active</span>
                </>
              )}
            </div>
            <p>
              {challan?.systemChallanNumber ? `${challan.systemChallanNumber} · ` : ""}
              {challan?.customerName || "—"} · {formatDate(challan?.date)}
            </p>
          </div>
          <div className="cv-header-actions">
            <button type="button" className="cv-btn cv-btn--primary" onClick={handleDownload}>
              <FiDownload size={15} /> Download
            </button>
            <button type="button" className="cv-btn" onClick={handlePrint}>
              <FiPrinter size={15} /> Print
            </button>
            <button type="button" className="cv-modal-close" onClick={onClose} aria-label="Close">
              <FiX size={18} />
            </button>
          </div>
        </div>

        <div className="cv-modal-body" id="challan-dialog-content" ref={printableRef}>
          {loading && (
            <div className="cv-modal-loading">
              <div className="cv-spinner" />
              Loading details...
            </div>
          )}

          {!loading && challan && (
            <>
              <div className="cv-summary cv-section">
                <div className="cv-stat-card">
                  <span className="cv-stat-icon">
                    <FiBox />
                  </span>
                  <div>
                    <span>Products</span>
                    <strong>{challan.items?.length || 0}</strong>
                  </div>
                </div>
                <div className="cv-stat-card">
                  <span className="cv-stat-icon cv-stat-icon--delivered">
                    <FiTruck />
                  </span>
                  <div>
                    <span>Delivered</span>
                    <strong>{challan.items?.reduce((sum, i) => sum + (i.deliveredQty || 0), 0) || 0}</strong>
                  </div>
                </div>
                <div className="cv-stat-card">
                  <span className="cv-stat-icon cv-stat-icon--received">
                    <FiRotateCcw />
                  </span>
                  <div>
                    <span>Received</span>
                    <strong>{challan.items?.reduce((sum, i) => sum + (i.receivedQty || 0), 0) || 0}</strong>
                  </div>
                </div>
              </div>

              <div className="cv-info-grid cv-section">
                <div className="cv-info-card">
                  <h4>
                    <span role="img" aria-label="General Information">📋</span>
                    General Information
                  </h4>
                  <div className="cv-info-fields">
                    <Field label="Challan No" value={challan.challanNumber} />
                    <Field label="Customer">
                      <div className="cv-field-customer">
                        <span className="cv-field-avatar">
                          <span className="cv-avatar-text">{challan.customerName?.charAt(0) || "?"}</span>
                        </span>
                        <strong>{challan.customerName || "—"}</strong>
                      </div>
                    </Field>
                    <Field label="Site Location" value={challan.siteLocation} />
                    <Field label="Date" value={formatDate(challan.date)} />
                    <Field label="Type">
                      <span className={typeChipClass(challan.challanType)}>{challan.challanType || "—"}</span>
                    </Field>
                  </div>
                </div>

                <div className="cv-info-card">
                  <h4>
                    <span role="img" aria-label="Transport Information">🚚</span>
                    Transport Information
                  </h4>
                  <div className="cv-info-fields">
                    <Field label="Vehicle" value={challan.vehicleNumber} />
                    <Field label="Driver" value={challan.driverName} />
                    <Field label="Driver Contact" value={challan.driverContact || challan.driverContactNumber || challan.driverPhoneNumber || challan.driverPhone || challan.driverNumber} />
                    <Field label="Transporter" value={challan.transporter} />
                    <Field label="PO Number" value={challan.purchaseOrderNo} />
                  </div>
                </div>
              </div>

              <div className="cv-section cv-items-section">
                <div className="cv-section-header">
                  <h4>Items</h4>
                  <span className="cv-item-count">
                    {challan.items.length} {challan.items.length === 1 ? 'Product' : 'Products'}
                  </span>
                </div>
                <div className="cv-table-wrap">
                  <table className="cv-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Product (Capacity)</th>
                        {challan.challanType === 'DELIVERED' && (
                          <>
                            <th>Delivered</th>
                            <th>Delivered SR</th>
                          </>
                        )}
                        {challan.challanType === 'RECEIVED' && (
                          <>
                            <th>Received</th>
                            <th>Received SR</th>
                          </>
                        )}
                        {challan.challanType === 'COMBINED' && (
                          <>
                            <th>Received</th>
                            <th>Delivered</th>
                            <th>Received SR</th>
                            <th>Delivered SR</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {challan.items?.map((item, index) => {
                        return (
                          <tr key={item.id ?? index}>
                            <td style={{ textAlign: 'left' }}>
                              <div className="cv-product-cell">
                                <div className="cv-product-icon"><FiBox size={16} /></div>
                                {item.name}{item.capacity && item.capacity !== '' && item.capacity !== null ? ` (${item.capacity})` : ''}
                              </div>
                            </td>
                            {challan.challanType === 'DELIVERED' && (
                              <>
                                <td>{item.deliveredQty || 0}</td>
                                <td style={{ color: '#15803D', fontWeight: 600 }}>
                                  {item.srNo && item.srNo.length > 0
                                    ? item.srNo.join(', ')
                                    : item.deliveredSrNos && item.deliveredSrNos.length > 0
                                    ? item.deliveredSrNos.join(', ')
                                    : '—'}
                                </td>
                              </>
                            )}
                            {challan.challanType === 'RECEIVED' && (
                              <>
                                <td>{item.receivedQty || 0}</td>
                                <td style={{ color: '#D97706', fontWeight: 600 }}>
                                  {item.srNo && item.srNo.length > 0
                                    ? item.srNo.join(', ')
                                    : item.receivedSrNos && item.receivedSrNos.length > 0
                                    ? item.receivedSrNos.join(', ')
                                    : '—'}
                                </td>
                              </>
                            )}
                            {challan.challanType === 'COMBINED' && (
                              <>
                                <td>{item.receivedQty || 0}</td>
                                <td>{item.deliveredQty || 0}</td>
                                <td style={{ color: '#D97706', fontWeight: 600 }}>
                                  {item.receivedSrNos && item.receivedSrNos.length > 0
                                    ? item.receivedSrNos.join(', ')
                                    : '—'}
                                </td>
                                <td style={{ color: '#15803D', fontWeight: 600 }}>
                                  {item.deliveredSrNos && item.deliveredSrNos.length > 0
                                    ? item.deliveredSrNos.join(', ')
                                    : '—'}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="cv-footer-grid cv-section">
                <div className="cv-footer-card">
                  <span>Delivery Details</span>
                  <p>{challan.deliveryDetails || "No Remarks"}</p>
                </div>
                <div className="cv-footer-card">
                  <span>Deposit/Return Amount</span>
                  <strong>
                    {challan.returnedAmount && challan.returnedAmount !== 0 ? 
                     (challan.returnedAmount > 0 ? `${challan.returnedAmount.toFixed(2)}` : `-${Math.abs(challan.returnedAmount).toFixed(2)}`) :
                     challan.deposit && challan.deposit !== 0 ? `${challan.deposit.toFixed(2)}` : 
                     '0.00'}
                  </strong>
                </div>
                <div className="cv-footer-card">
                  <span>Deposit Narration</span>
                  <p>{challan.depositeNarration || "No Narration"}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      <PdfPreviewModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfUrl={pdfUrl}
        fileName={`challan_${challan?.challanNumber || challanId}.pdf`}
      />
    </div>
    , document.body);
};

export default ChallanViewDialog;